// Roster-building statuses (scouting "Building roster" page) + shared minutes-% colour bands.
// Each status carries the expected share of season minutes and a hard cap on how many
// players should hold it across the whole roster.

export const DEFAULT_ROSTER_STATUSES = [
  { code: "key",       label: "Giocatrice chiave",             labelIt: "Giocatrice chiave",             max: 4,  hex: "#38bdf8" },
  { code: "starter",   label: "Titolare",                      labelIt: "Titolare",                      max: 3,  hex: "#22c55e" },
  { code: "turnover",  label: "Rotazione",                     labelIt: "Rotazione",                     max: 6,  hex: "#eab308" },
  { code: "potential", label: "Potenziale",                    labelIt: "Potenziale",                    max: 4,  hex: "#f97316" },
  { code: "talent",    label: "Talento / completamento rosa",  labelIt: "Talento / completamento rosa",  max: 12, hex: "#ef4444" },
];

// Active (per-club) statuses: hydrated from the backend after login so non-React consumers
// (e.g. the Starting XI image export) and statusHex() always reflect the club's config.
const _normalize = (s) => ({ ...s, label: s.label || s.labelIt, labelIt: s.labelIt || s.label });
let _active = DEFAULT_ROSTER_STATUSES.map(_normalize);
export const setActiveRosterStatuses = (list) => {
  _active = Array.isArray(list) && list.length ? list.map(_normalize) : DEFAULT_ROSTER_STATUSES.map(_normalize);
};
export const getActiveRosterStatuses = () => _active;

// Back-compat alias (defaults). Prefer getActiveRosterStatuses() / the RosterStatus context.
export const ROSTER_STATUSES = DEFAULT_ROSTER_STATUSES;

export const statusHex = (code) => _active.find((s) => s.code === code)?.hex || null;
export const statusLabel = (code) => _active.find((s) => s.code === code)?.labelIt || "";

// ---- Minutes played vs available ----------------------------------------
// % of available minutes actually played.
export const minutesPct = (played, avail) => {
  const p = Number(played), a = Number(avail);
  if (!a || a <= 0 || !Number.isFinite(p)) return null;
  return Math.round((p / a) * 100);
};

// Colour bands requested by staff: <25 red · 25–50 orange · 50–75 yellow · 75–85 green · 85–100 azure.
export const pctBand = (pct) => {
  if (pct == null) return { text: "text-muted-foreground", border: "border-border", bg: "bg-transparent", hex: "#94a3b8", cls: "text-muted-foreground border-border bg-transparent" };
  if (pct < 25) return { text: "text-red-400",    border: "border-red-400/40",    bg: "bg-red-500/10",    hex: "#f87171", cls: "text-red-400 border-red-400/40 bg-red-500/10" };
  if (pct < 50) return { text: "text-orange-400", border: "border-orange-400/40", bg: "bg-orange-500/10", hex: "#fb923c", cls: "text-orange-400 border-orange-400/40 bg-orange-500/10" };
  if (pct < 75) return { text: "text-yellow-400", border: "border-yellow-400/40", bg: "bg-yellow-500/10", hex: "#facc15", cls: "text-yellow-400 border-yellow-400/40 bg-yellow-500/10" };
  if (pct < 85) return { text: "text-green-400",  border: "border-green-400/40",  bg: "bg-green-500/10",  hex: "#4ade80", cls: "text-green-400 border-green-400/40 bg-green-500/10" };
  return { text: "text-sky-300", border: "border-sky-400/40", bg: "bg-sky-500/10", hex: "#38bdf8", cls: "text-sky-300 border-sky-400/40 bg-sky-500/10" };
};

export const pctClass = (pct) => pctBand(pct).cls;

// Scouting convention: every match offers 96 available minutes. Available minutes are derived
// from appearances (matches played) × 96, overriding any stored value when appearances exist.
export const SCOUT_MATCH_MINUTES = 96;
export const availMinutes = (player) => {
  const a = Number(player?.appearances);
  if (Number.isFinite(a) && a > 0) return a * SCOUT_MATCH_MINUTES;
  const stored = Number(player?.minutes_available);
  return Number.isFinite(stored) && stored > 0 ? stored : null;
};
export const scoutMinutesPct = (player) => { const p = minutesPct(player?.minutes, availMinutes(player)); return p == null ? null : Math.min(p, 100); };

// Minutes played over a season where every match offers 90 min and a player is capped at 90' per match.
// matches: array of objects having a `minutes` field (per match). Returns { played, available, pct }.
export const seasonMinutes = (perMatchMinutes, totalMatches) => {
  const played = Math.round((perMatchMinutes || []).reduce((a, m) => a + Math.min(Math.max(Number(m) || 0, 0), 90), 0));
  const available = (Number(totalMatches) || 0) * 90;
  return { played, available, pct: minutesPct(played, available) };
};
